import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import type { ValidateResult } from '../types';

interface ValidateStepProps {
  loading: boolean;
  result: ValidateResult | null;
  onValidate: () => void;
}

export default function ValidateStep({ loading, result, onValidate }: ValidateStepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">Step 4: Validate</h4>
        <p className="text-gray-600 text-sm">
          Run a dry-run against the server. No records are saved until you import.
        </p>
      </div>

      {!result && !loading && (
        <button type="button" onClick={onValidate} className="btn-primary inline-flex items-center gap-2">
          Run Validation
        </button>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-3" />
          <p className="text-gray-600 font-medium">Validating…</p>
        </div>
      )}

      {result && !loading && (
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg flex items-start gap-3 border ${
              result.ready_to_import
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {result.ready_to_import ? (
              <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" />
            )}
            <div>
              <h5 className="font-bold">{result.message}</h5>
              <p className="mt-1 text-sm">
                Valid: {result.valid_rows} / {result.total_rows}
                {typeof result.skipped === 'number' ? ` · Skipped: ${result.skipped}` : ''}
                {result.errors?.length ? ` · Errors: ${result.errors.length}` : ''}
                {result.warnings?.length ? ` · Warnings: ${result.warnings.length}` : ''}
              </p>
            </div>
          </div>

          {(result.errors?.length > 0 || result.warnings?.length > 0) && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-64">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Row</th>
                    <th className="text-left px-3 py-2 font-semibold">Field</th>
                    <th className="text-left px-3 py-2 font-semibold">Message</th>
                    <th className="text-left px-3 py-2 font-semibold">Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {[...(result.errors || []), ...(result.warnings || [])].map((err, idx) => (
                    <tr key={`${err.row}-${err.field}-${idx}`}>
                      <td className="px-3 py-2">{err.row}</td>
                      <td className="px-3 py-2">{err.field || '—'}</td>
                      <td className="px-3 py-2 text-red-700">{err.message}</td>
                      <td className="px-3 py-2 capitalize">{err.severity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button type="button" onClick={onValidate} className="btn-secondary">
            Re-validate
          </button>
        </div>
      )}
    </div>
  );
}
